import React from 'react';
import Button from '../components/ui/Button';
import { Link } from 'react-router-dom';

const AccountDeletion = () => {
  const handleEmailClick = () => {
    const subject = encodeURIComponent("DELETE ACCOUNT - ");
    const body = encodeURIComponent(
      "I would like to request permanent deletion of my TravelMaker account.\n\n" +
      "Please delete all my account data and associated information.\n\n" +
      "Thank you"
    );
    window.location.href = `mailto:info@travelmaker.app?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Header Section */}
      <div className="max-w-4xl mx-auto pt-20 pb-16 px-5 text-center">
        <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
          Delete Your Account
        </h1>
        <div className="w-24 h-1 bg-primary mx-auto rounded-full mb-6"></div>
        <p className="text-lg text-gray-600 mb-4">
          Request deletion of your TravelMaker account and associated data
        </p>
        <p className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-full inline-block">
          Last Updated: February 21, 2026
        </p>
      </div>

      {/* Content Section */}
      <div className="max-w-4xl mx-auto px-5 pb-20">

        <section className="mb-12">
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              TravelMaker respects your privacy and your right to control your personal data. If you wish to delete your account, you can do so at any time using one of the methods below.
            </p>
            <p>
              <strong>Please note:</strong> Account deletion is permanent and cannot be undone. All your account data and associated information will be permanently deleted.
            </p>
            <p>
              We offer two methods for account deletion:
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">Primary Method: Delete Within the App</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>The easiest way to delete your account is directly from the TravelMaker mobile app:</p>
            <ol className="ml-6 space-y-2 list-decimal">
              <li>Open the TravelMaker app on your device</li>
              <li>Go to Settings → Profile</li>
              <li>Tap 'Delete Account'</li>
            </ol>
            <p>
              <strong>Note:</strong> Your account deletion will be processed immediately after confirmation.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">Alternative Method: Request via Email</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              If you prefer, you can also request account deletion by sending an email to our support team. Click the button below to open your email client with a pre-filled deletion request:
            </p>

            <div className="flex justify-center my-6">
              <Button variant="primary" onClick={handleEmailClick} className="px-8 py-3">
                Send Deletion Request via Email
              </Button>
            </div>

            <p>
              <strong>If the button above doesn't work,</strong> you can manually send an email to:
            </p>
            <div className="bg-gray-50 py-4 px-6 rounded-lg">
              <p>
                <strong>Email:</strong> <a href="mailto:info@travelmaker.app" className="text-primary hover:text-primary/80">info@travelmaker.app</a><br />
                <strong>Subject:</strong> DELETE ACCOUNT - [your email address]<br />
                <strong>Message:</strong> Include your request to delete your account and all associated data
              </p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">What Data Will Be Deleted</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>
              When you delete your TravelMaker account, all your account data and associated information will be permanently deleted, including your profile, preferences, and activity history.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">Important Information</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Deletion is Permanent</h3>
            <p>
              Once your account is deleted, this action cannot be undone. You will lose access to all your saved data, preferences, and account information.
            </p>

            <h3 className="text-lg font-medium text-gray-900">Processing Timeline</h3>
            <p>
              Account deletion requests submitted via email are typically processed within 30 days. In-app deletions are processed immediately.
            </p>

            <h3 className="text-lg font-medium text-gray-900">No Recovery</h3>
            <p>
              After your account is deleted, we cannot recover your data. If you wish to use TravelMaker again in the future, you will need to create a new account.
            </p>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-900">Need Help?</h2>
          <div className="text-gray-700 leading-relaxed space-y-4">
            <p>If you have questions about account deletion or need assistance, please contact us:</p>
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <p>
                <strong>Company:</strong> Kolpo LTD<br />
                <strong>Email:</strong> <a href="mailto:info@travelmaker.app" className="text-primary hover:text-primary/80">info@travelmaker.app</a><br />
                <strong>Address:</strong> London, United Kingdom
              </p>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  For more information about how we handle your data, please review our{' '}
                  <Link to="/privacy" className="text-primary hover:text-primary/80">Privacy Policy</Link>
                  {' '}and{' '}
                  <Link to="/terms" className="text-primary hover:text-primary/80">Terms of Service</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AccountDeletion;
